import type { Trail } from '@shefira/shared';

/**
 * The sprites of a footstep trail (ADR 0073), without any drawing: where each
 * one is, which image of its sheet shows and how opaque, at a given instant.
 * Shared by the world, which draws them as Phaser images, and by the shop's
 * preview, on a DOM canvas. Purely visual: nothing here is ever sent anywhere.
 */

export interface TrailParticle {
  trail: Trail;
  /** Where it was left, in pixels: the feet. */
  x: number;
  y: number;
  /** Which look of the sheet it wears. */
  variant: number;
  bornAt: number;
  lifeMs: number;
  reach: number;
  /** A random 0..1 per sprite: its sway, its direction, its first image. */
  seed: number;
}

export interface TrailSprite {
  /** Centre of the sprite, whole pixels. */
  x: number;
  y: number;
  /** Image index in the sheet (`effect.trail-<id>/idle/<frame>`). */
  frame: number;
  alpha: number;
}

/** The sprites a puff leaves at `feet`, now. */
export function puffOf(
  trail: Trail,
  feet: { x: number; y: number },
  now: number,
  random: () => number = Math.random,
): TrailParticle[] {
  const particles: TrailParticle[] = [];
  for (let index = 0; index < trail.count; index += 1) {
    particles.push({
      trail,
      x: feet.x + (random() * 2 - 1) * trail.spread,
      // A little around the ground line: a trail is flat, seen from above.
      y: feet.y - 2 + (random() * 2 - 1) * 1.5,
      variant: Math.floor(random() * trail.variants),
      bornAt: now,
      // Not all at once: a trail that dies in step looks like a stamp.
      lifeMs: trail.lifeMs * (0.75 + random() * 0.5),
      reach: trail.reach * (0.7 + random() * 0.3),
      seed: random(),
    });
  }
  return particles;
}

/** Where a sprite is at `now`, and which image shows; null once its life is over. */
export function spriteOf(particle: TrailParticle, now: number): TrailSprite | null {
  const age = now - particle.bornAt;
  const progress = age / particle.lifeMs;
  if (progress < 0 || progress >= 1) return null;
  const { seed, reach, trail } = particle;
  // Each sprite starts its loop somewhere else, so a wake never blinks in step.
  let step = Math.floor(age / trail.frameMs + seed * trail.frames) % trail.frames;
  let dx = 0;
  let dy = 0;
  const alpha = progress < 0.6 ? 1 : 1 - (progress - 0.6) / 0.4;
  switch (trail.motion) {
    case 'rise':
      // Up from the feet, swaying a pixel either way as it goes.
      dy = -reach * easeOut(progress);
      dx = Math.sin(seed * 6.28 + progress * 9) * 1.5;
      break;
    case 'fall': {
      // Tumbling down from the waist in the first half, then lying flat.
      const drop = Math.min(1, progress * 2);
      dy = -reach * (1 - drop);
      dx = Math.sin(seed * 6.28 + drop * 7) * 3;
      if (drop >= 1) step = 0;
      break;
    }
    case 'settle':
      break;
    case 'drift': {
      // Along the ground, one way or the other, hopping over its own tumbles;
      // it comes to rest flat for its last moments.
      const side = seed < 0.5 ? -1 : 1;
      dx = side * reach * easeOut(progress);
      dy = -Math.abs(Math.sin(progress * 3 * Math.PI + seed * 3)) * 3 * (1 - progress);
      if (progress > 0.8) step = 0;
      break;
    }
  }
  return {
    x: Math.round(particle.x + dx),
    y: Math.round(particle.y + dy),
    frame: particle.variant * trail.frames + step,
    alpha: Math.max(0, alpha),
  };
}

function easeOut(progress: number): number {
  return 1 - (1 - progress) * (1 - progress);
}
